import { Download, FileUp, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/useIsMobile'
import { downloadCSVTemplate, TABLE_IMPORT_TEMPLATE_HEADERS } from '@/lib/csv'

interface ImportTablesDialogProps {
  isOpen: boolean
  onClose: () => void
  onImport: (file: File) => Promise<void>
  isPending: boolean
}

export const ImportTablesDialog = ({
  isOpen,
  onClose,
  onImport,
  isPending,
}: ImportTablesDialogProps) => {
  const [file, setFile] = useState<File | null>(null)
  const isMobile = useIsMobile()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0]
      if (
        selectedFile.type !== 'text/csv' &&
        !selectedFile.name.endsWith('.csv')
      ) {
        toast.error('กรุณาเลือกไฟล์ CSV เท่านั้น')
        return
      }
      setFile(selectedFile)
    }
  }

  const handleImport = async () => {
    if (!file) {
      toast.error('กรุณาเลือกไฟล์ก่อน')
      return
    }
    await onImport(file)
    setFile(null)
    onClose()
  }

  const handleDownloadTemplate = () => {
    downloadCSVTemplate('tables_template.csv', TABLE_IMPORT_TEMPLATE_HEADERS)
  }

  const content = (
    <div className="flex flex-col gap-6">
      <Button
        variant="outline"
        className="border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 h-12 w-full rounded-xl font-bold"
        onClick={handleDownloadTemplate}
      >
        <Download className="mr-2 size-5" />
        ดาวน์โหลดเทมเพลต CSV
      </Button>
      <div
        className={`border-primary/20 bg-primary/5 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed py-10 transition-colors ${
          file ? 'border-primary bg-primary/10' : ''
        }`}
      >
        <input
          type="file"
          id="csv-upload"
          className="sr-only"
          accept=".csv"
          onChange={handleFileChange}
        />
        <label
          htmlFor="csv-upload"
          className="flex cursor-pointer flex-col items-center gap-3"
        >
          <div className="bg-primary/20 text-primary flex size-14 items-center justify-center rounded-full">
            <FileUp className="size-7" />
          </div>
          <div className="text-center">
            <p className="text-base font-bold text-gray-900">
              {file ? file.name : 'คลิกเพื่อเลือกไฟล์ CSV'}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              รองรับไฟล์ .csv เท่านั้น
            </p>
          </div>
        </label>
      </div>

      <div className="flex flex-col gap-3 pt-2">
        <Button
          size="lg"
          className="bg-primary hover:bg-primary/90 shadow-primary/20 h-14 rounded-full text-lg font-bold shadow-lg"
          onClick={handleImport}
          disabled={!file || isPending}
        >
          {isPending ? 'กำลังนำเข้า...' : 'นำเข้าข้อมูล'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="rounded-full"
          onClick={onClose}
          disabled={isPending}
        >
          ยกเลิก
        </Button>
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
      >
        <DrawerContent className="pb-safe">
          <div className="mx-auto w-full max-w-lg p-6">
            <DrawerHeader className="mb-6 flex flex-row items-center justify-between p-0">
              <DrawerTitle className="text-xl font-bold">
                นำเข้าข้อมูลโต๊ะ
              </DrawerTitle>
              <DrawerClose className="bg-muted text-muted-foreground hover:text-foreground rounded-full p-2 transition-colors">
                <X className="size-5" />
              </DrawerClose>
            </DrawerHeader>
            <DrawerDescription className="mb-4 text-sm text-gray-500">
              อัปโหลดไฟล์ CSV เพื่อนำเข้าข้อมูลโต๊ะทั้งหมดในครั้งเดียว
              คุณสามารถดาวน์โหลดเทมเพลตเพื่อเตรียมข้อมูลให้ถูกต้อง
            </DrawerDescription>
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent
        className="sm:max-w-lg"
        showCloseButton={false}
      >
        <DialogHeader className="mb-4 flex flex-row items-center justify-between">
          <div className="flex flex-col gap-1">
            <DialogTitle className="text-xl font-bold">
              นำเข้าข้อมูลโต๊ะ
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              อัปโหลดไฟล์ CSV เพื่อนำเข้าข้อมูลโต๊ะทั้งหมดในครั้งเดียว
              คุณสามารถดาวน์โหลดเทมเพลตเพื่อเตรียมข้อมูลให้ถูกต้อง
            </DialogDescription>
          </div>
          <DialogClose className="bg-muted text-muted-foreground hover:text-foreground rounded-full p-2 transition-colors">
            <X className="size-5" />
          </DialogClose>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  )
}
